'use client'

import React, { useState, useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { Ticket, Sparkles, Clock, ArrowRight, ChevronRight, Eye, Zap, ShieldCheck, Gift } from 'lucide-react'
import { OffersOffer } from '@/services/dataTypes'
import {
    discardHTMLTags, getBaseImageUrl, getCurrencySymbol,
    getFinalDiscountTag, getMerchantHref, getProductDetailHref,
    calculateOfferDuration
} from '@/constants/hooks'
import Theme18OfferLink from './Theme18OfferLink'
import { copyToClipboard } from '../utils/clipboard'
import DiscountTag from './DiscountTag'

const ProductDetailsModal = dynamic(() => import('./ProductDetailsModal'), { ssr: false })

const C = {
    PRIMARY: '#F8F246',
    SECONDARY: '#2CBBC3',
    BROWN: '#8C5826',
    ORANGE: '#FF9934',
    BG: '#fffaf5',
    CARD_BG: '#ffffff',
    TEXT_DARK: '#1a1a1a',
    TEXT_MUTED: '#6b7280',
    BORDER: '#f0ede8',
    BORDER_HOVER: '#F8F246',
}

interface Props {
    offers: OffersOffer[]
    headingTitle?: string
    headingSubtitle?: string
    rawHeading?: string
    description?: string
    mer_slug: string
    mer_slug_type: string
    domain: string
    companyId: string
}

const PopularCouponsClient = ({
    offers = [],
    headingTitle,
    headingSubtitle,
    rawHeading,
    description,
    mer_slug,
    mer_slug_type,
    domain,
}: Props) => {
    const fullHeadingText = rawHeading || (headingTitle ? (headingSubtitle ? `${headingTitle} ${headingSubtitle}` : headingTitle) : 'Popular Coupons & Deals')
    const headingWords = fullHeadingText.trim().split(/\s+/)
    const splitIndex = Math.max(1, Math.ceil(headingWords.length / 2))
    const firstHeadingPart = headingWords.slice(0, splitIndex).join(' ')
    const secondHeadingPart = headingWords.slice(splitIndex).join(' ')

    const [copiedId, setCopiedId] = useState<string | number | null>(null)
    const [activeModalOffer, setActiveModalOffer] = useState<{
        offer: any
        merchantName: string
        merchantLogo: string
    } | null>(null)

    const handleCopyCode = async (e: React.MouseEvent, code: string, uniqueId: string | number) => {
        e.preventDefault()
        e.stopPropagation()
        await copyToClipboard(code.trim())
        setCopiedId(uniqueId)
        setTimeout(() => setCopiedId(null), 2000)
    }

    return (
        <section className="relative py-14 md:py-20 px-4 sm:px-6 overflow-hidden" style={{ background: 'linear-gradient(180deg, #fffaf5 0%, #ffffff 50%, #fffaf5 100%)' }}>

            {/* Ambient Lighting & Pet Care Glow Orbs */}
            <div className="absolute top-1/4 left-[-10%] w-[450px] h-[450px] rounded-full blur-3xl pointer-events-none opacity-20"
                style={{ background: 'radial-gradient(circle, #F8F246 0%, transparent 70%)' }} />
            <div className="absolute bottom-1/4 right-[-10%] w-[450px] h-[450px] rounded-full blur-3xl pointer-events-none opacity-15"
                style={{ background: 'radial-gradient(circle, #2CBBC3 0%, transparent 70%)' }} />

            <div className="relative max-w-7xl mx-auto">

                {/* ── Section Header ── */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8 md:mb-10">
                    <div className="space-y-3 max-w-4xl">

                        {/* Eyebrow Pill */}
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full shadow-xs"
                            style={{
                                background: '#ffffff',
                                border: '1px solid rgba(140, 88, 38, 0.15)',
                                boxShadow: '0 2px 10px rgba(140, 88, 38, 0.05)'
                            }}>
                            <span className="flex h-2 w-2 relative">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: C.ORANGE }} />
                                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: C.ORANGE }} />
                            </span>
                            <span className="text-[11px] font-black uppercase tracking-wider" style={{ color: C.BROWN }}>
                                Verified &amp; Tested Today
                            </span>
                            {offers.length > 0 && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${C.PRIMARY}80`, color: C.BROWN }}>
                                    {offers.length} Offers
                                </span>
                            )}
                        </div>

                        {/* Main Heading */}
                        <h2 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 leading-tight">
                            {firstHeadingPart}{' '}
                            {secondHeadingPart && (
                                <span style={{ color: C.BROWN }}>
                                    {secondHeadingPart}
                                </span>
                            )}
                        </h2>

                        {/* Subtitle / Description */}
                        <p className="text-sm md:text-base leading-relaxed line-clamp-3" style={{ color: C.TEXT_MUTED }}>
                            {description || 'Discover handpicked coupons, instant voucher codes, and verified discounts from top-rated stores.'}
                        </p>
                    </div>

                    {/* Right Header Action Button */}
                    <div className="flex items-center gap-3 flex-shrink-0 self-start lg:self-auto">
                        <Link
                            href="/category"
                            className="group inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl font-black text-xs sm:text-sm transition-all duration-300 hover:scale-105 shadow-md hover:shadow-xl whitespace-nowrap"
                            style={{
                                background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffd452 100%)`,
                                color: C.BROWN,
                                border: '1px solid rgba(140, 88, 38, 0.15)',
                                boxShadow: '0 4px 20px rgba(248, 242, 70, 0.45)',
                            }}
                        >
                            <Sparkles size={16} className="text-[#8C5826]" />
                            <span>Browse All Deals</span>
                            <ChevronRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </div>

                {/* ── Coupons & Deals Grid ── */}
                {offers.length === 0 ? (
                    <div className="py-16 flex flex-col items-center justify-center text-center rounded-3xl bg-white border border-[#f0ede8] p-8 shadow-xs">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: `${C.PRIMARY}40` }}>
                            <Ticket size={24} style={{ color: C.BROWN }} />
                        </div>
                        <h3 className="text-sm font-bold text-gray-900">No active offers currently available</h3>
                        <p className="text-xs text-gray-400 mt-1 max-w-xs">Check back soon for freshly updated discount coupons.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {offers.map((item, idx) => {
                            const product = item?.offer
                            const merchant = item?.merchant
                            const isCoupon = !!product?.coupon_code
                            const isProduct = product?.offer_type?.name === 'product'
                            const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
                            const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
                            const discountPct = originalPrice > 0 && salePrice > 0
                                ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
                                : null
                            const finalDiscountTag = getFinalDiscountTag(product?.offer_title || product?.offer_detail, discountPct)
                            const currencySymbol = getCurrencySymbol(product?.currency)
                            const merchantHref = getMerchantHref(merchant, mer_slug, mer_slug_type)
                            const productDetailUrl = product?.slug
                                ? getProductDetailHref(merchant, mer_slug_type, product.slug, product?.category?.slug)
                                : null
                            const imageUrl = getBaseImageUrl(domain, merchant?.merchant_logo ?? '', '')
                            const imgSrc = (product?.offer_type?.name === 'product' && product?.product_image)
                                ? getBaseImageUrl(domain, product.product_image, '')
                                : imageUrl
                            const duration = calculateOfferDuration(product?.end_date ?? null)
                            const isExpired = duration === 'Offer expired'
                            const uniqueId = product?.unique_id || product?.id || idx

                            return (
                                <article
                                    key={uniqueId}
                                    className="group relative bg-white rounded-3xl overflow-hidden flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border border-[#f0ede8] hover:border-[#F8F246]"
                                    style={{
                                        boxShadow: '0 4px 20px rgba(140, 88, 38, 0.04)',
                                    }}
                                >
                                    {/* ── Card Hero: Image with Badges (Matching Theme 5 - No Store Name Header) ── */}
                                    <div className="relative w-full h-48 sm:h-52 bg-gradient-to-br from-[#fffdfa] via-[#fcfbf9] to-[#f9f8f5] flex items-center justify-center p-4 border-b border-[#f5f2eb] overflow-hidden group-hover:border-[#F8F246]/50 transition-colors">

                                        {/* Radial Accent */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-[#F8F246]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                                        <Image
                                            src={imgSrc}
                                            alt={discardHTMLTags(product?.offer_title) || 'Product deal'}
                                            fill
                                            className="object-contain p-6 group-hover:scale-110 transition-transform duration-500 z-10 drop-shadow-xs"
                                        />

                                        {/* Discount Tag Over Image (Top Left) */}
                                        {finalDiscountTag && (
                                            <div className="absolute top-2.5 left-2.5 z-20">
                                                <DiscountTag tag={finalDiscountTag} />
                                            </div>
                                        )}

                                        {/* Type / Duration Badge (Top Right) */}
                                        {isCoupon ? (
                                            <span className="absolute top-3 right-3 z-20 text-[9px] font-black px-2.5 py-1 rounded-xl shadow-xs tracking-wider flex items-center gap-1"
                                                style={{ background: C.PRIMARY, color: C.BROWN }}>
                                                <Ticket size={10} /> CODE
                                            </span>
                                        ) : duration ? (
                                            <span className="absolute top-3 right-3 z-20 text-[9px] font-bold px-2 py-0.5 rounded-lg bg-white/90 text-gray-700 backdrop-blur-sm border border-gray-200 flex items-center gap-1 shadow-xs">
                                                <Clock size={10} /> {duration}
                                            </span>
                                        ) : null}
                                    </div>

                                    {/* ── Card Content ── */}
                                    <div className="px-5 pt-4 pb-3 flex flex-col flex-1 gap-2.5">

                                        {/* Title (Centered) */}
                                        {product?.is_detail === 1 && productDetailUrl ? (
                                            <Link href={productDetailUrl} className="block">
                                                <h3 className="text-sm font-bold text-gray-900 leading-snug line-clamp-2 text-center hover:text-[#8C5826] transition-colors">
                                                    {discardHTMLTags(product?.offer_title)}
                                                </h3>
                                            </Link>
                                        ) : (
                                            <Theme18OfferLink
                                                unique_id={product?.unique_id}
                                                outUrl={product?.url || merchantHref}
                                                merchantHref={merchantHref}
                                                domain={domain}
                                                couponCode={product?.coupon_code || undefined}
                                            >
                                                <h3 className="text-sm font-bold text-gray-900 leading-snug line-clamp-2 text-center hover:text-[#8C5826] transition-colors">
                                                    {discardHTMLTags(product?.offer_title)}
                                                </h3>
                                            </Theme18OfferLink>
                                        )}

                                        {/* Price Row (Centered) */}
                                        {salePrice > 0 && (
                                            <div className="flex items-baseline justify-center gap-2 pt-1 text-center">
                                                <span className="text-lg font-black text-gray-900">
                                                    {currencySymbol}{salePrice.toFixed(2)}
                                                </span>
                                                {originalPrice > 0 && originalPrice > salePrice && (
                                                    <span className="text-xs line-through text-gray-400">
                                                        {currencySymbol}{originalPrice.toFixed(2)}
                                                    </span>
                                                )}
                                                {discountPct && (
                                                    <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-600 border border-emerald-100">
                                                        Save {discountPct}%
                                                    </span>
                                                )}
                                            </div>
                                        )}



                                        {/* Expiry & View Details (Right aligned like Theme 5) */}
                                        <div className="flex items-center justify-between gap-2 pt-2 mt-auto text-xs text-gray-500 border-t border-gray-100">
                                            <span className="inline-flex items-center gap-1 font-medium">
                                                <Clock size={12} className={isExpired ? 'text-rose-500' : 'text-gray-400'} />
                                                <span className={isExpired ? 'text-rose-600 font-bold' : ''}>
                                                    {duration}
                                                </span>
                                            </span>
                                            <button
                                                onClick={() => setActiveModalOffer({
                                                    offer: product,
                                                    merchantName: merchant?.merchant_name || 'Store',
                                                    merchantLogo: imageUrl,
                                                })}
                                                className="inline-flex items-center gap-1 text-xs text-[#8C5826] hover:text-[#5e3814] cursor-pointer hover:underline font-bold transition-colors"
                                            >
                                                <Eye size={12} />
                                                <span>View Details</span>
                                            </button>
                                        </div>

                                        {/* Main CTA (Theme-5 button logic) */}
                                        <Theme18OfferLink
                                            unique_id={product?.unique_id}
                                            outUrl={product?.url || merchantHref}
                                            merchantHref={merchantHref}
                                            domain={domain}
                                            couponCode={product?.coupon_code || undefined}
                                            customClass="block w-full"
                                        >
                                            <div
                                                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-xs font-black uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] shadow-sm hover:shadow-md cursor-pointer"
                                                style={
                                                    isCoupon
                                                        ? {
                                                            background: C.BROWN,
                                                            color: '#ffffff',
                                                            boxShadow: '0 4px 14px rgba(140, 88, 38, 0.25)',
                                                        }
                                                        : {
                                                            background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffd452 100%)`,
                                                            color: C.BROWN,
                                                            border: '1px solid rgba(140, 88, 38, 0.15)',
                                                            boxShadow: '0 4px 14px rgba(248, 242, 70, 0.35)',
                                                        }
                                                }
                                            >
                                                <span>{isCoupon ? 'Show Coupon' : (isProduct ? 'Buy Now' : 'Grab Deal')}</span>
                                                <ArrowRight size={13} />
                                            </div>
                                        </Theme18OfferLink>
                                    </div>
                                </article>
                            )
                        })}
                    </div>
                )}

                {/* ── Bottom Futuristic Trust Ticker Bar ── */}
                <div className="mt-14 rounded-3xl p-6 md:p-8 bg-white border border-[#f0ede8] shadow-sm">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                        {[
                            { icon: ShieldCheck, title: '100% Tested Daily', desc: 'Real-time voucher verification' },
                            { icon: Ticket, title: 'Instant Code Copy', desc: 'Auto-copied directly to clipboard' },
                            { icon: Zap, title: 'Direct Store Deals', desc: 'Official merchant affiliate discounts' },
                            { icon: Gift, title: 'Free Pet Goodies', desc: 'Free delivery & sample offers' },
                        ].map((item, idx) => {
                            const Icon = item.icon
                            return (
                                <div key={idx} className="flex flex-col items-center gap-2">
                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm"
                                        style={{ background: `${C.PRIMARY}35`, color: C.BROWN }}>
                                        <Icon size={22} style={{ color: C.BROWN }} />
                                    </div>
                                    <h4 className="text-xs md:text-sm font-black text-gray-900">{item.title}</h4>
                                    <p className="text-[11px] text-gray-500">{item.desc}</p>
                                </div>
                            )
                        })}
                    </div>
                </div>

            </div>

            {/* ── Controlled Product Details Modal ── */}
            {activeModalOffer && (
                <ProductDetailsModal
                    product={activeModalOffer.offer}
                    merchantName={activeModalOffer.merchantName}
                    merchantLogo={activeModalOffer.merchantLogo}
                    domain={domain}
                    isOpen={true}
                    onClose={() => setActiveModalOffer(null)}
                />
            )}

        </section>
    )
}

export default PopularCouponsClient
